import {
  BreakpointKey,
  GridPosition,
  PlacedWidget,
  PlacedContainer,
  PlacedItem,
  WidgetInstance,
  ContainerInstance,
  WidgetSizeConfig,
} from '../types';
import { getWidget } from '../registry';
import { getContainer } from '../containerRegistry';
import { getWidgetSizeForBreakpoint, getWidgetSizeWithVariant, GRID_COLUMNS } from './breakpoints';

// ============================================================================
// Grid Engine - Collision Detection Layout
// ============================================================================

/**
 * Calculate layout for widgets using collision detection.
 * Each widget is placed in the first available position that doesn't
 * overlap with any existing widgets.
 */
export function calculateLayout(
  widgets: WidgetInstance[],
  breakpoint: BreakpointKey
): PlacedWidget[] {
  // Sort widgets by order
  const sortedWidgets = [...widgets].sort((a, b) => a.order - b.order);

  // Track occupied cells using a Set with "col,row" keys
  const occupied = new Set<string>();
  const result: PlacedWidget[] = [];

  for (const widget of sortedWidgets) {
    const definition = getWidget(widget.widgetId);
    if (!definition) {
      console.warn(`Widget definition not found: ${widget.widgetId}`);
      continue;
    }

    // Get size considering user's selected variant from settings
    // Supports phone/tablet/desktop variants for responsive layouts
    const selectedVariant = widget.settings?.sizeVariant;
    const phoneSizeVariant = widget.settings?.phoneSizeVariant;
    const tabletSizeVariant = widget.settings?.tabletSizeVariant;
    const size = getWidgetSizeWithVariant(
      definition.sizes,
      definition.sizeVariants,
      selectedVariant,
      breakpoint,
      phoneSizeVariant,
      tabletSizeVariant
    );

    // Find first available position using collision detection
    const position = findNextAvailablePosition(occupied, size.cols, size.rows);

    // Mark all cells as occupied
    markCellsOccupied(occupied, position.col, position.row, size.cols, size.rows);

    result.push({
      instanceId: widget.instanceId,
      widgetId: widget.widgetId,
      position: {
        col: position.col,
        row: position.row,
        width: size.cols,
        height: size.rows,
      },
    });
  }

  return result;
}

/**
 * Find the next available position for a widget of given dimensions.
 * Scans row by row, column by column to find the first position where
 * all cells needed by the widget are free.
 */
function findNextAvailablePosition(
  occupied: Set<string>,
  width: number,
  height: number
): { col: number; row: number } {
  const maxRows = 100; // Safety limit

  for (let row = 0; row < maxRows; row++) {
    for (let col = 0; col <= GRID_COLUMNS - width; col++) {
      if (canPlaceWidget(occupied, col, row, width, height)) {
        return { col, row };
      }
    }
  }

  // Fallback: place at next row after all occupied cells
  return { col: 0, row: getMaxOccupiedRow(occupied) + 1 };
}

/**
 * Check if a widget can be placed at the given position without overlapping.
 */
function canPlaceWidget(
  occupied: Set<string>,
  startCol: number,
  startRow: number,
  width: number,
  height: number
): boolean {
  // Check bounds
  if (startCol < 0 || startCol + width > GRID_COLUMNS) {
    return false;
  }

  // Check all cells the widget would occupy
  for (let r = startRow; r < startRow + height; r++) {
    for (let c = startCol; c < startCol + width; c++) {
      if (occupied.has(`${c},${r}`)) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Mark cells as occupied.
 */
function markCellsOccupied(
  occupied: Set<string>,
  startCol: number,
  startRow: number,
  width: number,
  height: number
): void {
  for (let r = startRow; r < startRow + height; r++) {
    for (let c = startCol; c < startCol + width; c++) {
      occupied.add(`${c},${r}`);
    }
  }
}

/**
 * Get the maximum occupied row number.
 */
function getMaxOccupiedRow(occupied: Set<string>): number {
  let maxRow = -1;
  for (const key of occupied) {
    const [, rowStr] = key.split(',');
    const row = parseInt(rowStr, 10);
    maxRow = Math.max(maxRow, row);
  }
  return maxRow;
}

// ============================================================================
// Combined Layout (Widgets + Containers)
// ============================================================================

/**
 * Calculate combined layout for both widgets and containers.
 * Widgets and containers are sorted by their order property and placed
 * using collision detection.
 */
export function calculateCombinedLayout(
  widgets: WidgetInstance[],
  containers: ContainerInstance[],
  breakpoint: BreakpointKey
): PlacedItem[] {
  // Combine widgets and containers with their order values
  type OrderedItem =
    | { type: 'widget'; order: number; data: WidgetInstance }
    | { type: 'container'; order: number; data: ContainerInstance };

  const orderedItems: OrderedItem[] = [
    ...widgets.map((w) => ({ type: 'widget' as const, order: w.order, data: w })),
    ...containers.map((c) => ({ type: 'container' as const, order: c.order, data: c })),
  ];

  // Sort by order
  orderedItems.sort((a, b) => a.order - b.order);

  // Track occupied cells
  const occupied = new Set<string>();
  const result: PlacedItem[] = [];

  for (const item of orderedItems) {
    if (item.type === 'widget') {
      const widget = item.data;
      const definition = getWidget(widget.widgetId);
      if (!definition) {
        console.warn(`Widget definition not found: ${widget.widgetId}`);
        continue;
      }

      // Get size considering user's selected variant
      const size = getWidgetSizeWithVariant(
        definition.sizes,
        definition.sizeVariants,
        widget.settings?.sizeVariant,
        breakpoint,
        widget.settings?.phoneSizeVariant,
        widget.settings?.tabletSizeVariant
      );

      // Find position and place
      const position = findNextAvailablePosition(occupied, size.cols, size.rows);
      markCellsOccupied(occupied, position.col, position.row, size.cols, size.rows);

      result.push({
        type: 'widget',
        item: {
          instanceId: widget.instanceId,
          widgetId: widget.widgetId,
          position: {
            col: position.col,
            row: position.row,
            width: size.cols,
            height: size.rows,
          },
        },
      });
    } else {
      const container = item.data;
      const definition = getContainer(container.containerId);
      if (!definition) {
        console.warn(`Container definition not found: ${container.containerId}`);
        continue;
      }

      // Get size considering user's selected variant
      const size = getWidgetSizeWithVariant(
        definition.sizes,
        definition.sizeVariants,
        container.settings?.sizeVariant,
        breakpoint,
        container.settings?.phoneSizeVariant,
        container.settings?.tabletSizeVariant
      );

      // Find position and place
      const position = findNextAvailablePosition(occupied, size.cols, size.rows);
      markCellsOccupied(occupied, position.col, position.row, size.cols, size.rows);

      result.push({
        type: 'container',
        item: {
          instanceId: container.instanceId,
          containerId: container.containerId,
          position: {
            col: position.col,
            row: position.row,
            width: size.cols,
            height: size.rows,
          },
        },
      });
    }
  }

  return result;
}

/**
 * Calculate total grid height needed for combined layout.
 */
export function calculateTotalRowsFromCombined(layout: PlacedItem[]): number {
  let maxRow = 0;
  for (const placed of layout) {
    const position = placed.item.position;
    const endRow = position.row + position.height;
    maxRow = Math.max(maxRow, endRow);
  }
  return maxRow;
}

// ============================================================================
// Layout Utilities
// ============================================================================

/**
 * Calculate total grid height needed for the layout in rows.
 */
export function calculateTotalRows(layout: PlacedWidget[]): number {
  let maxRow = 0;
  for (const widget of layout) {
    const endRow = widget.position.row + widget.position.height;
    maxRow = Math.max(maxRow, endRow);
  }
  return maxRow;
}

/**
 * Check if two widgets overlap.
 */
export function widgetsOverlap(a: GridPosition, b: GridPosition): boolean {
  return !(
    a.col + a.width <= b.col ||
    b.col + b.width <= a.col ||
    a.row + a.height <= b.row ||
    b.row + b.height <= a.row
  );
}

/**
 * Get widget at a specific grid cell position.
 */
export function getWidgetAtPosition(
  layout: PlacedWidget[],
  col: number,
  row: number
): PlacedWidget | undefined {
  return layout.find((widget) => {
    const { position } = widget;
    return (
      col >= position.col &&
      col < position.col + position.width &&
      row >= position.row &&
      row < position.row + position.height
    );
  });
}

/**
 * Calculate where a widget would be inserted based on drag position.
 */
export function calculateInsertIndex(
  layout: PlacedWidget[],
  widgets: WidgetInstance[],
  dropCol: number,
  dropRow: number
): number {
  // Find widgets that would be after this position
  const sortedWidgets = [...widgets].sort((a, b) => a.order - b.order);

  for (let i = 0; i < layout.length; i++) {
    const placedWidget = layout[i];
    // If the drop position is before this widget's position
    if (
      dropRow < placedWidget.position.row ||
      (dropRow === placedWidget.position.row && dropCol < placedWidget.position.col)
    ) {
      return i;
    }
  }

  return sortedWidgets.length;
}

/**
 * Recalculate layout after a widget is moved.
 */
export function recalculateLayoutAfterMove(
  widgets: WidgetInstance[],
  movedWidgetId: string,
  newIndex: number,
  breakpoint: BreakpointKey
): PlacedWidget[] {
  // Reorder widgets
  const reorderedWidgets = [...widgets];
  const currentIndex = reorderedWidgets.findIndex((w) => w.instanceId === movedWidgetId);

  if (currentIndex === -1) return calculateLayout(widgets, breakpoint);

  const [moved] = reorderedWidgets.splice(currentIndex, 1);
  reorderedWidgets.splice(newIndex, 0, moved);

  // Update order values
  const updatedWidgets = reorderedWidgets.map((w, idx) => ({ ...w, order: idx }));

  return calculateLayout(updatedWidgets, breakpoint);
}

// ============================================================================
// Size Calculation Helpers
// ============================================================================

/**
 * Get widget sizes for the given breakpoint.
 */
export function getWidgetSizes(
  widgets: WidgetInstance[],
  breakpoint: BreakpointKey
): Map<string, { cols: number; rows: number }> {
  const sizes = new Map<string, { cols: number; rows: number }>();

  for (const widget of widgets) {
    const definition = getWidget(widget.widgetId);
    if (definition) {
      const size = getWidgetSizeForBreakpoint(definition.sizes, breakpoint);
      sizes.set(widget.instanceId, size);
    }
  }

  return sizes;
}
